/**
 * LiveKit Voice Chat Provider Implementation
 *
 * Implements the VoiceChatProvider interface using LiveKit SDK.
 * This is the concrete implementation for LiveKit provider.
 */

import {
  Room,
  RoomEvent,
  Participant as LKParticipant,
  RoomOptions,
  LocalTrackPublication,
  RemoteTrackPublication,
  Track,
  ConnectionState
} from 'livekit-client';

import {
  VoiceChatProvider,
  ConnectionConfig,
  Participant,
  VoiceChatEvent,
  EventHandler
} from '../../../types/voice-chat-provider';

export class LiveKitProvider implements VoiceChatProvider {
  private room: Room | null = null;
  private eventHandlers: Map<string, Set<EventHandler>> = new Map();
  private audioUnlockHandler: (() => void) | null = null;

  async connect(config: ConnectionConfig): Promise<void> {
    if (this.room) {
      await this.disconnect();
    }

    const roomOptions: RoomOptions = {
      ...config.options
    };

    this.room = new Room(roomOptions);

    this.setupEventListeners();

    await this.room.connect(config.url, config.token);

    // Unlock remote audio on first user gesture (click/touch/keydown).
    // Browsers block AudioContext until a user interaction occurs.
    this.removeAudioUnlockHandler();
    const { room } = this;
    this.audioUnlockHandler = () => {
      room.startAudio().catch((err) => {
        console.warn('[LiveKit] startAudio on user gesture failed:', err);
      });
      this.removeAudioUnlockHandler();
    };
    document.addEventListener('click', this.audioUnlockHandler, { once: true });
    document.addEventListener('touchstart', this.audioUnlockHandler, { once: true });

    this.emit('connected', { room: this.room });
  }

  async disconnect(): Promise<void> {
    if (this.room) {
      await this.room.disconnect();
      this.room = null;
    }
    this.emit('disconnected', {});
  }

  async setMicrophoneEnabled(enabled: boolean): Promise<void> {
    if (!this.room) {
      throw new Error('Not connected');
    }
    await this.room.localParticipant.setMicrophoneEnabled(enabled);
    // Called from a user gesture (button click), so startAudio() can
    // reliably resume the AudioContext and unlock remote audio playback.
    await this.room.startAudio();
    this.emit('microphone_changed', { enabled });
  }

  getParticipants(): Participant[] {
    if (!this.room) {
      return [];
    }
    const participants: Participant[] = [];

    const { localParticipant } = this.room;
    if (localParticipant) {
      participants.push(this.mapParticipant(localParticipant));
    }

    Array.from(this.room.remoteParticipants.values()).forEach((participant) => {
      participants.push(this.mapParticipant(participant));
    });

    return participants;
  }

  getActiveSpeakers(): Participant[] {
    if (!this.room) {
      return [];
    }
    return this.room.activeSpeakers.map((speaker) => this.mapParticipant(speaker));
  }

  on(event: VoiceChatEvent, handler: EventHandler): void {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, new Set());
    }
    this.eventHandlers.get(event)!.add(handler);
  }

  off(event: VoiceChatEvent, handler: EventHandler): void {
    this.eventHandlers.get(event)?.delete(handler);
  }

  private setupEventListeners(): void {
    if (!this.room) {
      return;
    }

    this.room.on(RoomEvent.ParticipantConnected, (participant: LKParticipant) => {
      this.emit('participant_joined', this.mapParticipant(participant));
    });

    this.room.on(RoomEvent.ParticipantDisconnected, (participant: LKParticipant) => {
      this.emit('participant_left', this.mapParticipant(participant));
    });

    this.room.on(RoomEvent.ActiveSpeakersChanged, (speakers: LKParticipant[]) => {
      this.emit(
        'speaker_changed',
        speakers.map((speaker) => this.mapParticipant(speaker))
      );
    });

    this.room.on(RoomEvent.Disconnected, () => {
      this.emit('disconnected', {});
    });

    this.room.on(RoomEvent.RoomMetadataChanged, () => {});

    this.room.on(RoomEvent.ConnectionStateChanged, (state: ConnectionState) => {
      if (state === ConnectionState.Disconnected) {
        this.emit('disconnected', {});
      } else if (state === ConnectionState.Connected) {
        this.emit('connected', { room: this.room });
      }
    });

    this.room.on(RoomEvent.LocalTrackPublished, (publication: LocalTrackPublication, participant: LKParticipant) => {
      if (publication.kind === Track.Kind.Audio) {
        this.emit('microphone_changed', { enabled: true });
      }
      this.emit('participant_joined', this.mapParticipant(participant));
    });

    this.room.on(RoomEvent.LocalTrackUnpublished, (publication: LocalTrackPublication, participant: LKParticipant) => {
      if (publication.kind === Track.Kind.Audio) {
        this.emit('microphone_changed', { enabled: false });
      }
      this.emit('participant_joined', this.mapParticipant(participant));
    });

    this.room.on(RoomEvent.TrackPublished, (publication: RemoteTrackPublication, participant: LKParticipant) => {
      this.emit('participant_joined', this.mapParticipant(participant));
    });

    this.room.on(RoomEvent.TrackUnpublished, (publication: RemoteTrackPublication, participant: LKParticipant) => {
      this.emit('participant_joined', this.mapParticipant(participant));
    });

    this.room.on(
      RoomEvent.TrackSubscribed,
      (track: Track, publication: RemoteTrackPublication, participant: LKParticipant | undefined) => {
        if (track.kind === Track.Kind.Audio && participant) {
          const audioElement = track.attach();
          document.body.appendChild(audioElement);
          // Use startAudio() instead of manual play() — it handles browser
          // autoplay policy by resuming AudioContext and setting up a
          // user-gesture listener to retry if playback is initially blocked.
          this.room?.startAudio().catch((error) => {
            console.warn('[LiveKit] Failed to start audio:', error);
          });
        }

        if (participant) {
          this.emit('participant_joined', this.mapParticipant(participant));
        }
      }
    );

    this.room.on(
      RoomEvent.TrackUnsubscribed,
      (track: Track, publication: RemoteTrackPublication, participant: LKParticipant | undefined) => {
        if (track.kind === Track.Kind.Audio) {
          track.detach();
        }

        if (participant) {
          this.emit('participant_joined', this.mapParticipant(participant));
        }
      }
    );
  }

  private mapParticipant(lkParticipant: LKParticipant): Participant {
    const { isLocal } = lkParticipant;
    const isSpeaking = this.room?.activeSpeakers.includes(lkParticipant) || false;

    return {
      id: lkParticipant.identity,
      name: lkParticipant.name || lkParticipant.identity,
      isSpeaking,
      audioLevel: lkParticipant.audioLevel,
      isLocal
    };
  }

  private emit(event: string, data: unknown): void {
    this.eventHandlers.get(event as VoiceChatEvent)?.forEach((handler) => {
      try {
        handler(data);
      } catch (error) {
        console.error(`Error in event handler for ${event}:`, error);
      }
    });
  }

  private removeAudioUnlockHandler(): void {
    if (this.audioUnlockHandler) {
      document.removeEventListener('click', this.audioUnlockHandler);
      document.removeEventListener('touchstart', this.audioUnlockHandler);
      this.audioUnlockHandler = null;
    }
  }

  destroy(): void {
    this.removeAudioUnlockHandler();
    this.disconnect().catch((error) => {
      console.error('Error during disconnect:', error);
    });
    this.eventHandlers.clear();
  }
}
